import { isObject } from "lodash";

export const INITIALIZE = "INITIALIZE";
export const ADD_TO_SELECTED_ITEMS = "ADD_TO_SELECTED_ITEMS";
export const REMOVE_ITEM_FROM_OPTIONS = "REMOVE_ITEM_FROM_OPTIONS";
export const ADD_ITEM_TO_OPTIONS = "ADD_ITEM_TO_OPTIONS";
export const REMOVE_FROM_SELECTED_ITEMS = "REMOVE_FROM_SELECTED_ITEMS";
export const MOVE_SELECTED_ITEMS = "MOVE_SELECTED_ITEMS";
export const MOVE_OPTIONS = "MOVE_OPTIONS";

const updateFieldValue = state => {
  const { options, selectedItems: values, updateValue, onChange } = state;
  const optionsCount = options.length + values.length;

  updateValue({ values, optionsCount });
  if (onChange) onChange({ values, optionsCount });

  return state;
};

const initialize = (state, { inputOptions, inputValues }) => {
  const selectedItems = inputValues.map((optionOrId, position) => {
    if (isObject(optionOrId)) return optionOrId;

    // Pre-filled answers only store option ids, so we take the whole option to keep all
    // its displayed data, like its description, when a member resumes the ranking.
    const option = inputOptions.find(({ id }) => optionOrId === id);

    return { ...option, id: optionOrId, position, isSelected: true };
  });

  const selectedItemIds = selectedItems.map(({ id }) => id);

  const options = inputOptions
    .map((option, position) => ({
      ...option,
      position,
      isSelected: false
    }))
    .filter(({ id }) => !selectedItemIds.includes(id));

  return updateFieldValue({ ...state, options, selectedItems });
};

const addToSelectedItems = (state, { droppedItem }) => {
  const { selectedItems } = state;

  const newSelectedItem = {
    ...droppedItem,
    position: selectedItems.length,
    isSelected: true
  };

  // Avoid adding an item twice to the list
  const selectedItemIds = selectedItems.map(({ id }) => id);
  if (selectedItemIds.includes(droppedItem.id)) return state;

  const newSelectedItems = [...selectedItems, newSelectedItem];

  return updateFieldValue({ ...state, selectedItems: newSelectedItems });
};

const removeItemFromOptions = (state, { droppedItem }) => {
  const { options } = state;

  const newOptions = options
    .filter(({ id }) => id !== droppedItem.id)
    .map((option, position) => ({
      ...option,
      position
    }));

  return { ...state, options: newOptions };
};

const addItemToOptions = (state, { droppedItem }) => {
  const { options } = state;

  const newOption = {
    ...droppedItem,
    position: options.length,
    isSelected: false
  };

  // Avoid adding an item twice to the list
  const optionsIds = options.map(({ id }) => id);
  if (optionsIds.includes(droppedItem.id)) return state;

  const newOptions = [...options, newOption];
  return { ...state, options: newOptions };
};

const removeFromSelectedItems = (state, { itemToRemove }) => {
  const { selectedItems } = state;

  const newSelectedItems = selectedItems
    .filter(option => option.id !== itemToRemove.id)
    .map((option, index) => ({
      ...option,
      position: index
    }));

  return updateFieldValue({ ...state, selectedItems: newSelectedItems });
};

const moveSelectedItems = (state, { draggedItem, sourceIndex, targetIndex }) => {
  const selectedItems = [...state.selectedItems];
  const movedItem = { ...draggedItem };
  selectedItems.splice(sourceIndex, 1);
  selectedItems.splice(targetIndex, 0, movedItem);

  const newSelectedItems = selectedItems.map((item, index) => ({
    ...item,
    position: index
  }));

  return updateFieldValue({ ...state, selectedItems: newSelectedItems });
};

const moveOptions = (state, { draggedItem, sourceIndex, targetIndex }) => {
  const newOptions = [...state.options];
  const movedItem = { ...draggedItem };
  newOptions.splice(sourceIndex, 1);
  newOptions.splice(targetIndex, 0, movedItem);

  const reorderedNewOptions = newOptions.map((option, index) => ({
    ...option,
    position: index
  }));

  return { ...state, options: reorderedNewOptions };
};

export const SortableListActions = (state, { type, payload }) => {
  switch (type) {
    case INITIALIZE:
      return initialize(state, payload);

    case ADD_TO_SELECTED_ITEMS:
      return addToSelectedItems(state, payload);

    case REMOVE_ITEM_FROM_OPTIONS:
      return removeItemFromOptions(state, payload);

    case ADD_ITEM_TO_OPTIONS:
      return addItemToOptions(state, payload);

    case REMOVE_FROM_SELECTED_ITEMS:
      return removeFromSelectedItems(state, payload);

    case MOVE_SELECTED_ITEMS:
      return moveSelectedItems(state, payload);

    case MOVE_OPTIONS:
      return moveOptions(state, payload);

    default:
      return state;
  }
};
